import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CloudinaryImage from "./CloudinaryImage";
import StrapiImage from "./StrapiImage";
import RallyRoadOverlay from "./RallyRoadOverlay";
import StickyHeadline from "./StickyHeadline";
import { Headline } from "./ui";
import { api, transformBlogPost } from "../services/api";
import "./BlogPostCarousel.css";

// Dummy data for debug mode
const dummyBlogPosts = [
  {
    id: 1,
    title: "Porsche 911 Turbo S: Der ultimative Sportwagen-Test",
    slug: "porsche-911-turbo-s-test",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323651/gallery/frank_kayser/M10_6886_ivl5s1.jpg",
    publishDate: "2024-03-15",
    aspectRatio: 0.85,
  },
  {
    id: 2,
    title: "Restauration eines klassischen Porsche 356",
    slug: "restauration-porsche-356",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323590/gallery/frank_kayser/M10_7558_taux5a.jpg",
    publishDate: "2024-03-08",
    aspectRatio: 1.0,
  },
  {
    id: 3,
    title: "Leistungssteigerung für Porsche Boxster: Was ist möglich?",
    slug: "leistungssteigerung-porsche-boxster",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323940/gallery/cars/watercooled_marketing/1_sb3ocu",
    publishDate: "2024-03-02",
    aspectRatio: 0.85,
  },
  {
    id: 4,
    title: "Porsche Cayenne: Wartung und häufige Probleme",
    slug: "porsche-cayenne-wartung-probleme",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323400/gallery/frank_kayser/L1013228_yfgfsy.jpg",
    publishDate: "2024-02-25",
    aspectRatio: 0.75,
  },
  {
    id: 5,
    title: "Die Evolution der Porsche 911 Motoren",
    slug: "evolution-porsche-911-motoren",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323475/gallery/frank_kayser/M10_6747_mnlim4.jpg",
    publishDate: "2024-02-18",
    aspectRatio: 0.75,
  },
  {
    id: 6,
    title: "Porsche im Motorsport: Von Le Mans bis zur Formel 1",
    slug: "porsche-motorsport-le-mans-formel-1",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323701/gallery/frank_kayser/M10_6908_jiwup7.jpg",
    publishDate: "2024-02-10",
    aspectRatio: 0.85,
  },
  {
    id: 7,
    title: "Porsche Macan: Der perfekte SUV für Enthusiasten?",
    slug: "porsche-macan-suv-enthusiasten",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323404/gallery/frank_kayser/M10_7587_w4gz6d",
    publishDate: "2024-02-03",
    aspectRatio: 0.75,
  },
  {
    id: 8,
    title: "Elektrifizierung bei Porsche: Der Taycan und die Zukunft",
    slug: "elektrifizierung-porsche-taycan-zukunft",
    heroImage:
      "https://res.cloudinary.com/donwh3eei/image/upload/v1756323486/gallery/frank_kayser/M10_7409_q5qdyx.jpg",
    publishDate: "2024-01-28",
    aspectRatio: 1.0,
  },
];

const BlogPostCarousel = ({
  posts: externalPosts,
  darkMode = false,
  debugMode = false,
}) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [posts, setPosts] = useState([]);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Fetch blog posts from Strapi, use external posts, or use dummy data in debug mode
  useEffect(() => {
    const fetchBlogPosts = async () => {
      if (debugMode) {
        // Use dummy data in debug mode
        setPosts(dummyBlogPosts);
        return;
      }

      if (externalPosts) {
        setPosts(externalPosts);
        return;
      }

      try {
        // Fetch up to 16 posts from Strapi
        const response = await api.getBlogPosts(1, 16);
        const transformedPosts = response.data.map(transformBlogPost);
        setPosts(transformedPosts);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setPosts([]);
      }
    };

    fetchBlogPosts();
  }, [externalPosts, debugMode]);

  // Scroll progress tracking
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = wrapper;
      const maxScroll = scrollWidth - clientWidth;
      // Add offset so marker starts 120px from left instead of 0
      const offsetProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      // Map 0-1 progress to offset-1 range (120px offset proportional to viewport)
      const offsetRatio = 120 / window.innerWidth; // Dynamic offset based on viewport
      const adjustedProgress = offsetRatio + offsetProgress * (1 - offsetRatio);
      setScrollProgress(Math.min(1, Math.max(offsetRatio, adjustedProgress)));
    };

    wrapper.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => {
      wrapper.removeEventListener("scroll", handleScroll);
    };
  }, [posts]);

  const handlePostClick = (post) => {
    if (post.slug) {
      navigate(`/blog/${post.slug}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
      .toUpperCase();
  };

  return (
    <div
      className={`blog-carousel-container ${darkMode ? "dark-mode" : ""}`}
      style={darkMode ? { backgroundColor: "var(--color-primary)" } : {}}
    >
      <StickyHeadline
        headline="Werkstatt Journal"
        navbarHeight={70}
        className="mb-8"
      />
      <Headline
        variant="splitscreen"
        color="secondary"
        className=" pt-16 sm:mt-32  px-4 sm:px-24"
      >
        Unsere Geschichten
      </Headline>
      <RallyRoadOverlay scrollProgress={scrollProgress} />
      <div className="blog-carousel-wrapper sm:pl-12" ref={wrapperRef}>
        <div className="blog-carousel-track mb-12 sm:mb-16">
          {posts && posts.length > 0 ? (
            posts.map((post, index) => (
              <div
                key={post.id || index}
                className="blog-post-card cursor-pointer hover:opacity-80 transition-opacity"
                style={{ "--aspect-ratio": post.aspectRatio || 1.6 }}
                onClick={() => handlePostClick(post)}
              >
                <div className="blog-post-image-container">
                  {post.heroImage ? (
                    post.heroImage.startsWith("http") ? (
                      <CloudinaryImage
                        src={post.heroImage}
                        alt={post.title}
                        className="blog-post-image"
                        width={800}
                        height={Math.round(800 / (post.aspectRatio || 1.6))}
                        crop="fill"
                        gravity="auto"
                      />
                    ) : (
                      <StrapiImage
                        src={post.heroImage}
                        alt={post.title}
                        className="blog-post-image"
                        width={800}
                        height={Math.round(800 / (post.aspectRatio || 1.6))}
                      />
                    )
                  ) : (
                    <div className="blog-post-image bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400">No Image</span>
                    </div>
                  )}
                </div>
                <div className="blog-post-content">
                  <div
                    className={`text-base sm:text-xl font-light leading-tight mb-2.5 text-left line-clamp-4 ${
                      darkMode ? "text-[var(--color-secondary)]" : "text-black"
                    }`}
                  >
                    {post.title}
                  </div>
                  <p
                    className={`text-xs sm:text-base font-bold uppercase text-left ${
                      darkMode
                        ? "text-[var(--color-secondary)]"
                        : "text-[var(--color-primary)]"
                    }`}
                  >
                    {formatDate(post.publishDate)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center py-20">
              <p className={darkMode ? "text-white" : "text-gray-600"}>
                Keine Blog-Beiträge verfügbar
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPostCarousel;
